import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppBar, AppIcon, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import {
  getViewAsComparativeListApi,
  getViewAsPaidListApi,
  getViewAsProvisionalListApi,
} from "../../api/finance.income.api";
import { EMP_BASE_PATH } from "../../config";

const TEXT = "#08142D";

// Ở chế độ kiểm tra giao dịch dùng bộ endpoint checkTransaction (VIEW_AS_* của app).
const BOXES = [
  { kind: "tam-tinh", icon: appImages.icon_TTKQ, label: "Thu nhập tạm tính", api: getViewAsProvisionalListApi },
  { kind: "doi-chieu", icon: appImages.icon_HDNV, label: "Thu nhập đối chiếu", api: getViewAsComparativeListApi },
  {
    kind: "chi-tra",
    icon: appImages.icon_HDMB,
    label: "Thu nhập chi trả",
    api: getViewAsPaidListApi,
    extraParams: { paymentStatus: "PARTIAL" },
  },
];

// Ô "Thu nhập chờ chi trả" tách riêng khỏi BOXES vì luồng Kiểm tra giao dịch
// (xem theo góc nhìn nhân viên) chưa có API tương ứng -> chỉ hiện khi xem của mình.
const UNPAID_BOX = {
  path: "cho-chi-tra",
  icon: appImages.icon_thu_nhap,
  label: "Thu nhập chờ chi trả",
};

const MenuCard = ({ icon, label, onClick }) => (
  <Flex
    as="button"
    type="button"
    onClick={onClick}
    w="48%"
    direction="column"
    justify="space-between"
    h="96px"
    p="14px"
    borderRadius="12px"
    bg="white"
    border="1px solid"
    borderColor="#E5E9F0"
    textAlign="left"
    boxShadow="0px 5px 5px rgba(0,0,0,0.04)"
  >
    <AppIcon src={icon} size={32} color={APP.primary200} />
    <Text fontSize="13px" fontWeight={600} color={TEXT}>
      {label}
    </Text>
  </Flex>
);

const IncomeIndex = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const base = `${EMP_BASE_PATH}/tai-chinh/thu-nhap`;

  // Kiểm tra giao dịch: có employeeCode -> xem theo góc nhìn nhân viên (khớp app).
  const employeeCode = search.get("employeeCode") || "";
  const apartmentCode = search.get("apartmentCode") || "";
  const isViewAs = !!employeeCode;

  // id của bản ghi đầu tiên theo từng loại; undefined = căn không có dữ liệu loại đó.
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchKind = useCallback(
    async (box) => {
      try {
        const data = await box.api({
          page: 1,
          limit: 20,
          employeeCode,
          apartmentCode: apartmentCode || undefined,
          ...(box.extraParams || {}),
        });
        return data?.items?.[0]?._id;
      } catch {
        return undefined;
      }
    },
    [employeeCode, apartmentCode]
  );

  // Nạp trước cả 3 loại để chỉ hiện ô nào thực sự có dữ liệu - khớp app.
  useEffect(() => {
    if (!isViewAs) return undefined;
    let active = true;
    setAvailability(null);
    setLoading(true);
    Promise.all(BOXES.map(fetchKind))
      .then((ids) => {
        if (!active) return;
        setAvailability(BOXES.reduce((acc, b, i) => ({ ...acc, [b.kind]: ids[i] }), {}));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isViewAs, fetchKind]);

  const viewAsQuery = useMemo(() => {
    const q = new URLSearchParams();
    if (employeeCode) q.set("employeeCode", employeeCode);
    if (apartmentCode) q.set("apartmentCode", apartmentCode);
    const s = q.toString();
    return s ? `?${s}` : "";
  }, [employeeCode, apartmentCode]);

  const onPressCard = (box) => {
    if (!isViewAs) {
      navigate(`${base}/${box.kind}`);
      return;
    }
    // Ở chế độ kiểm tra giao dịch: mở thẳng chi tiết giao dịch đầu tiên của căn.
    const id = availability?.[box.kind];
    if (!id) return;
    navigate(`${base}/${box.kind}/${id}${viewAsQuery}`);
  };

  const visibleBoxes = isViewAs ? BOXES.filter((b) => !!availability?.[b.kind]) : BOXES;

  const pageBody = (
    <>
        {loading ? (
          <Flex justify="center" py="32px">
            <Spinner color={APP.primary200} />
          </Flex>
        ) : isViewAs && availability && visibleBoxes.length === 0 ? (
          <Box py="24px">
            <Text fontSize="10px" fontWeight={500} color="#848484">
              Không có dữ liệu thu nhập cho căn này
            </Text>
          </Box>
        ) : (
          <Flex wrap="wrap" gap="12px">
            {visibleBoxes.map((box) => (
              <MenuCard key={box.kind} icon={box.icon} label={box.label} onClick={() => onPressCard(box)} />
            ))}
            {!isViewAs ? (
              <MenuCard
                key="unpaid"
                icon={UNPAID_BOX.icon}
                label={UNPAID_BOX.label}
                onClick={() => navigate(`${base}/${UNPAID_BOX.path}`)}
              />
            ) : null}
          </Flex>
        )}
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage
        title="Thu nhập cá nhân"
        right={isViewAs ? null : <DesktopBack onClick={() => navigate(EMP_BASE_PATH)} />}
      >
        <DesktopPanel>{pageBody}</DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh">
      <AppBar title="Thu nhập cá nhân" onBack={isViewAs ? undefined : () => navigate(EMP_BASE_PATH)} />
      <Box px="16px" pt="12px">
        {pageBody}
      </Box>
    </Box>
  );
};

export default IncomeIndex;
